import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface Props {
  storeId?: string;                // preselected store, optional
  availableStores?: { id: string; name: string }[];
  showAlways?: boolean;            // if true, always render (for NetworkView)
  onAlertCreated?: () => void; 
}

export default function TrainingSimulation({ storeId: initialStoreId, availableStores, showAlways, onAlertCreated }: Props) {
  const { user } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState(initialStoreId || '');
  const [pois, setPois] = useState<any[]>([]);
  const [selectedPoiId, setSelectedPoiId] = useState('');
  const [alertType, setAlertType] = useState<'guardian' | 'access_control'>('guardian');
  const [imageUrl, setImageUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedImagePoiId, setSelectedImagePoiId] = useState('');

  // Fetch POIs when store changes
  useEffect(() => {
    if (!selectedStoreId) return;
    api.getStorePois(selectedStoreId).then(data => {
      setPois(data);
      if (data.length > 0) setSelectedPoiId(data[0].id);
      else setSelectedPoiId('');
    }).catch(console.error);
  }, [selectedStoreId]);

  // Auto‑select first POI for Access Control, just like Guardian
  useEffect(() => {
    if (alertType === 'access_control' && pois.length > 0) {
      const firstPoi = pois[0];
      setSelectedImagePoiId(firstPoi.id);
      setImageUrl(firstPoi.photoUrl || '');
    } else if (alertType === 'access_control' && pois.length === 0) {
      setSelectedImagePoiId('');
      setImageUrl('');
    }
  }, [alertType, pois]);


  const handleTrigger = async () => {
    if (!selectedStoreId) return;
    setSending(true);
    try {
      if (alertType === 'guardian') {
        if (!selectedPoiId) return;
        await api.createAlert(selectedPoiId, selectedStoreId);
        onAlertCreated?.();
      } else {
        await api.simulateAccessAlert(selectedStoreId, imageUrl || 'https://via.placeholder.com/300?text=Unauthorized');
        onAlertCreated?.(); 
      }
      alert('Alert sent! Guards will be notified.');
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  // If a specific store is forced (e.g. admin), use it and hide the selector
  useEffect(() => {
    if (initialStoreId) {
      setSelectedStoreId(initialStoreId);
    }
  }, [initialStoreId]);

  // v0.4.9.2.4c13: a TAdmin must never see this, whatever the caller asks.
  //
  // `showAlways` makes the guard below unreachable (it is ANDed, so
  // `!showAlways` is false whenever a caller passes showAlways), and
  // NetworkView does pass it — which rendered the whole Training
  // Simulation panel for every role including TAdmin. This line is the
  // backstop; NetworkView also stops rendering it.
  //
  // Training Simulation fires simulated Guardian / Access Control alerts
  // to validate a client's installation. That is a client-admin and
  // employee QA activity, not part of a TAdmin's provisioning scope.
  if (user?.role === 'tadmin') return null;

  if (!showAlways && !user?.trainingModeEnabled) return null; // hidden for non‑premium clients

  return (
    <Card className="p-4 my-4">
      <h3 className="font-semibold mb-3">Training Simulation</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Show store selector only when there are multiple stores to choose from */}
      {availableStores && availableStores.length > 0 && !initialStoreId ? (
        <div className="col-span-2 md:col-span-1">
          <label className="text-sm">Store</label>
          <select
            className="w-full mt-1 p-2 border rounded"
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
          >
            <option value="" disabled>Select store</option>
            {availableStores.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      ) : (
        initialStoreId ? (
          <div className="col-span-2 md:col-span-1">
            <label className="text-sm">Store</label>
            <p className="text-sm font-medium mt-1 p-2 border rounded bg-muted/30">
              {availableStores?.[0]?.name || 'Current Store'}
            </p>
          </div>
        ) : (
          <div className="col-span-2 md:col-span-1">
            <label className="text-sm">Store ID</label>
            <input
              className="w-full mt-1 p-2 border rounded"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              placeholder="Enter store ID"
            />
          </div>
        )
      )}
        <div className="col-span-2 md:col-span-1">
          <label className="text-sm">Alert Type</label>
          <select
            className="w-full mt-1 p-2 border rounded"
            value={alertType}
            onChange={(e) => setAlertType(e.target.value as any)}
          >
            <option value="guardian">Guardian (POI)</option>
            <option value="access_control">Access Control</option>
          </select>
        </div>
        {alertType === 'guardian' && selectedStoreId && (
          <div className="col-span-2">
            <label className="text-sm">Select POI</label>
            <select
              className="w-full mt-1 p-2 border rounded"
              value={selectedPoiId}
              onChange={(e) => setSelectedPoiId(e.target.value)}
              disabled={pois.length === 0}
            >
              {pois.length === 0 && <option value="">No POIs</option>}
              {pois.map(poi => (
                <option key={poi.id} value={poi.id}>{poi.code}</option>
              ))}
            </select>
          </div>
        )}
        {alertType === 'access_control' && (
        <div className="col-span-2">
            <label className="text-sm">Select a POI</label>
            <select
            className="w-full mt-1 p-2 border rounded"
            value={selectedImagePoiId}
            onChange={(e) => {
                setSelectedImagePoiId(e.target.value);
                const poi = pois.find(p => p.id === e.target.value);
                setImageUrl(poi ? poi.photoUrl : '');
            }}
            >
            <option value="">— Choose a POI —</option>
            {pois.map(poi => (
                <option key={poi.id} value={poi.id}>{poi.code}</option>
            ))}
            </select>
        </div>
        )}
        <div className="col-span-2 flex justify-end">
          <Button
          disabled={!selectedStoreId || (alertType === 'guardian' && !selectedPoiId) || (alertType === 'access_control' && !selectedImagePoiId) || sending}            onClick={handleTrigger}
          >
            {sending ? 'Sending...' : 'Trigger Alert'}
          </Button>
        </div>
      </div>
    </Card>
  );
}